import { useEffect, useState } from 'react';
import { STUDENT_STATUSES, STUDENT_STATUS_LABEL } from '@doit/shared';
import { Modal } from '../../components/Modal';
import { Button, Field } from '../../components/ui';
import { Select, Textarea } from '../../components/form';
import { useToast } from '../../components/Toast';
import { useFormErrors } from '../../lib/useFormErrors';
import { useSaveStudent } from '../../api/queries';
import type { StudentStatus } from '@doit/shared';

/** მინიმალური ფორმა, რომელიც ფორმას სჭირდება — სიაც და ქარდიც ერგება */
export type EditableStudent = {
  id: string;
  fullName: string;
  email: string | null;
  academyEmail: string | null;
  phone: string | null;
  status: StudentStatus;
  personalId?: string | null;
  birthDate?: string | null;
  note?: string | null;
};

export function StudentModal({
  open,
  onClose,
  student,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  student: EditableStudent | null;
  onSaved?: (id: string) => void;
}) {
  const toast = useToast();
  const save = useSaveStudent();
  const { fieldErrors, reset, handle } = useFormErrors();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [academyEmail, setAcademyEmail] = useState('');
  const [personalId, setPersonalId] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [status, setStatus] = useState<string>('ACTIVE');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    reset();
    setFullName(student?.fullName ?? '');
    setPhone(student?.phone ?? '');
    setEmail(student?.email ?? '');
    setAcademyEmail(student?.academyEmail ?? '');
    setPersonalId(student?.personalId ?? '');
    setBirthDate(student?.birthDate ? student.birthDate.slice(0, 10) : '');
    setStatus(student?.status ?? 'ACTIVE');
    setNote(student?.note ?? '');
  }, [open, student, reset]);

  async function submit() {
    reset();
    try {
      const saved = await save.mutateAsync({
        id: student?.id,
        data: { fullName, phone, email, academyEmail, personalId, birthDate, status, note },
      });
      toast.success(student ? 'სტუდენტი განახლდა' : 'სტუდენტი დაემატა');
      onClose();
      onSaved?.(saved.id);
    } catch (err) {
      handle(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={student ? 'სტუდენტის რედაქტირება' : 'ახალი სტუდენტი'}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            გაუქმება
          </Button>
          <Button size="sm" onClick={() => void submit()} loading={save.isPending}>
            შენახვა
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="სახელი გვარი"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={fieldErrors.fullName}
          autoFocus
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="ტელეფონი"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={fieldErrors.phone}
            placeholder="599 12 34 56"
            hint="დუბლიკატები ნომრით მოწმდება"
          />
          <Select
            label="სტატუსი"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={STUDENT_STATUSES.map((s) => ({ value: s, label: STUDENT_STATUS_LABEL[s] }))}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="პირადი ელფოსტა"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors.email}
          />
          <Field
            label="აკადემიის ელფოსტა"
            type="email"
            value={academyEmail}
            onChange={(e) => setAcademyEmail(e.target.value)}
            error={fieldErrors.academyEmail}
            placeholder="n.surname.doit4@itstep.ge"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="პირადი ნომერი"
            value={personalId}
            onChange={(e) => setPersonalId(e.target.value)}
            error={fieldErrors.personalId}
          />
          <Field
            label="დაბადების თარიღი"
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            error={fieldErrors.birthDate}
          />
        </div>

        <Textarea
          label="შენიშვნა"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          error={fieldErrors.note}
        />
      </div>
    </Modal>
  );
}
